import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { JurisdictionRiskConfig, QualitativeConfigApi, ConsequenceRating } from 'src/app/models/jurisdictionRiskConfig.model';
import { TenantRisk } from 'src/app/models/tenantRisk.model';
import { BayesianConfig } from 'src/app/models/bayesian-config.model';
import { QualitativeMatrixConfig, QualitativeDimensionOption, LikelihoodMapping } from 'src/app/models/qualitativeMatrix.model';
import { CensoredDataMethod, CENSORED_DATA_METHOD_OPTIONS } from 'src/app/enums/censoredDataMethod.enum';
import { MethodOfAssessment } from 'src/app/enums/methodOfAssessment.enum';
import { JurisdictionRiskConfigService } from 'src/app/services/Jurisdiction-risk-config.service';
import { TenantRiskService } from 'src/app/services/tenantRisk.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { PermissionService } from 'src/app/services/permission.service';
import {
    DEFAULT_COLOR,
    DEFAULT_NEW_LEVEL_COLOR,
    DEFAULT_NEW_LIKELIHOOD_COLOR,
    UNMATCHED_SCORE_COLOR
} from 'src/app/shared/constants/risk-matrix.constants';
import { RiskLegendItem } from 'src/app/models/risk-config-legend.model';

interface RiskConfigSource {
    riskLevels?: string;
    exposureLikelihoodLevels?: string;
    qualitativeConfig?: string | QualitativeConfigApi;
    bayesianConfig?: string;
    defaultPriorMode?: number;
    defaultPriorWidth?: number;
    enableCensoringGuardrail?: boolean;
    configurationNotes?: string;
    isBayesianAnalysisEnabled?: boolean | null;
    matrixExceedanceDimension?: number;
    matrixFrequencyDimension?: number;
    censoredDataMethod?: number;
    consequenceMethodMapping?: string;
}

export interface RiskLevelItem {
    key: string;
    Name: string;
    minThresholds: number;
    maxThresholds: number;
    Color: string;
    ReqMethodOfAssessment: number;
    ControlAdequacy: number;
    PostAssessmentControlAdequacy: number;
    Description?: string;
}

export interface ExposureLikelihoodLevelItem {
    key: string;
    Name: string;
    QuantitativeName?: string;
    minQualThreshold: number;
    maxQualThreshold: number;
    minQuantThreshold: number;
    maxQuantThreshold: number;
    MinSamples: number;
    Color: string;
    ReassessmentFrequencyMonths: number;
    Description?: string;
}

export interface SuggestedQualThreshold {
    levelIndex: number;
    levelName: string;
    currentMin: number;
    currentMax: number;
    suggestedMin: number;
    suggestedMax: number;
    color: string;
}

export interface ExposureLikelihoodLevelConfig {
    Name: string;
    QuantitativeName?: string;
    QualThresholds: [number, number];
    QuantThreshold_OEL: [number, number];
    MinSamples: number;
    Color: string;
    ReassessmentFrequencyMonths: number;
    Description?: string;
}

export interface RiskLevelConfig {
    Name: string;
    Thresholds: [number, number];
    Color: string;
    ReqMethodOfAssessment: number;
    ControlAdequacy: number;
    PostAssessmentControlAdequacy: number;
    Description?: string;
}

interface ExposureLikelihoodLevelsStorage {
    [key: string]: ExposureLikelihoodLevelConfig | ConsequenceRating[];
    ConsequenceRatings?: ConsequenceRating[];
}

@Component({
  standalone: false,
    selector: 'app-jurisdiction-risk-config-form',
    templateUrl: './jurisdiction-risk-config-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./jurisdiction-risk-config-form.component.scss'],
})
export class JurisdictionRiskConfigFormComponent implements OnInit {
    @Input() jurisdictionId: string;
    @Input() bayesianOnly: boolean = false;

    riskLevels: RiskLevelItem[] = [];
    exposureLikelihoodLevels: ExposureLikelihoodLevelItem[] = [];
    consequenceRatings: ConsequenceRating[] = [];
    qualitativeMatrixConfig: QualitativeMatrixConfig = this.initializeDefaultQualitativeConfig();
    pipelineNodes: { id: string; label: string; icon: string }[] = [
        { id: 'section-nondetect-handling', label: 'Non-Detect Handling', icon: 'rule' },
        { id: 'section-assessment-dimensions', label: 'Assessment Dimensions', icon: 'straighten' },
        { id: 'section-qualitative-matrix', label: 'Qualitative Matrix', icon: 'grid_3x3' },
        { id: 'section-likelihood-levels', label: 'Likelihood Levels', icon: 'analytics' },
        { id: 'section-risk-levels', label: 'Risk Levels', icon: 'warning' }
    ];
    tenantRisk: TenantRisk;
    useEN689: boolean = true;
    isCustomConfig: boolean = false;
    isLoading: boolean = false;
    hasChanges: boolean = false;

    showPipelineHIW: boolean = false;
    showRiskHowItWorks: boolean = false;

    jurisdictionConfig: JurisdictionRiskConfig | null;

    matrixExceedanceDimension: number = 5;  // rows (probability)
    matrixFrequencyDimension: number = 5;   // columns (frequency)
    // Required option count per dimension, indexed by dimension order
    // [exceedance, frequency]. Precomputed so the template can read
    // dimensionRequiredCounts[dimIndex] instead of repeating the index ternary
    // (and without a template function call).
    dimensionRequiredCounts: number[] = [this.matrixExceedanceDimension, this.matrixFrequencyDimension];
    proposedQualThresholds: SuggestedQualThreshold[] = [];
    showThresholdSuggestion: boolean = false;
    thresholdSuggestionPending: boolean = false;
    readonly matrixDimensionOptions = [3, 4, 5, 6, 7, 8, 9, 10];
    // Permission gating for the mutating controls (Create from Defaults, Reset to
    // Defaults, Save, EN 689 toggle). See DASH-3101. Read-only users see the config
    // but cannot trigger a mutation from the UI. hasPermissionFlag is fail-closed, so
    // a role with no explicit JurisdictionRiskConfig grant resolves to "may not edit".
    readonly riskConfigPermissionObject = RolePermissionObjects.JurisdictionRiskConfig;
    readonly createAction = GridPermissions.Create;
    readonly updateAction = GridPermissions.Update;
    // Whether the current user may edit this jurisdiction's risk config. Mutating controls (e.g. the
    // EN 689 toggle) stay visible for read-only users but are disabled, rather than disappearing.
    canEditRiskConfig = false;
    readonly deleteAction = GridPermissions.Delete;
    // G19/DASH-2977 (FE-3): non-detect (censored-data) handling. Governs ALL exposure statistics,
    // so it lives on the standard (non-Bayesian) view, upstream of every scoring section.
    censoredDataMethod: CensoredDataMethod = CensoredDataMethod.LodOverSqrt2;
    readonly censoredDataMethodOptions = CENSORED_DATA_METHOD_OPTIONS;
    readonly censoredDataMethodEnum = CensoredDataMethod;
    readonly reassessFrequencyTooltip = 'Based on Geometric Mean (GM) vs OEL per EN 689: GM < 0.1×OEL = 36 months, 0.1–0.25×OEL = 24 months, 0.25–0.5×OEL = 18 months, >0.5×OEL = 12 months';
    // DASH-3163: the per-band Min Samp column is authoritative only when EN 689 is disabled for the
    // jurisdiction; it accepts 1–1000, matching the server-side validation.
    readonly minSampColumnTooltip = 'Minimum sample count for this band (1–1000). Applies only when EN 689 Compliance Testing is disabled for this jurisdiction; while EN 689 is enabled these values are ignored.';
    bayesianEnabled: boolean = false;
    bayesianFramework: string = 'Bayesian Exposure Assessment';
    bayesianDefaultPriorMode: number = 0;
    // Informative (QRA) prior temporarily disabled — force uninformative everywhere
    // until the informative pipeline ships.
    readonly bayesianInformativePriorDisabled = true;
    // Narrow prior width temporarily disabled — force wide until the narrow
    // distribution ships.
    readonly bayesianNarrowPriorDisabled = true;
    bayesianDefaultPriorWidth: 'wide' | 'narrow' = 'wide';
    bayesianInformativePriorSource: string = 'QRA';
    bayesianEnableCensoringGuardrail: boolean = true;
    bayesianNotes: string = '';
    bayesianQuantBandsReference: Array<{ name: string; color: string; range: string }> = [];
    private _prevMaxProduct: number = this.matrixExceedanceDimension * this.matrixFrequencyDimension;

    private matrixPreviewCache: { product: number; color: string; mapping: LikelihoodMapping | null }[][] = [];
    private axisLabels0Cache: QualitativeDimensionOption[] = [];
    private axisLabels1Cache: QualitativeDimensionOption[] = [];
    consequenceAxisCache: { value: number; label: string }[] = [];
    riskMatrixCache: { likelihoodLabel: string; cells: { score: number; color: string }[] }[] = [];
    // DASH-3171: pre-computed legend items for the shared <app-risk-config-legend>. Rebuilt on every
    // config change so the template never calls a builder method.
    likelihoodQualLegendItems: RiskLegendItem[] = [];
    likelihoodQuantLegendItems: RiskLegendItem[] = [];
    riskLevelLegendItems: RiskLegendItem[] = [];

    constructor(
        private jurisdictionRiskConfigService: JurisdictionRiskConfigService,
        private tenantRiskService: TenantRiskService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private permissionService: PermissionService
    ) { }

    ngOnInit(): void {
        this.canEditRiskConfig = this.permissionService.hasPermissionFlag(
            this.riskConfigPermissionObject,
            gridToPermissionFlags[GridPermissions.Update],
        );
        this.loadConfigurations();
    }

    loadConfigurations(): void {
        this.isLoading = true;

        this.jurisdictionRiskConfigService.getByJurisdictionId(this.jurisdictionId).subscribe({
            next: (response) => {
                this.jurisdictionConfig = response;
                this.isCustomConfig = !!response?.id;
                this.useEN689 = response?.useEN689 ?? true;
                if (response) {
                    this.parseConfigurations(response);
                }
                this.loadTenantDefaults();
            },
            error: (error) => {
                this.isCustomConfig = false;
                this.loadTenantDefaults();
            }
        });
    }

    loadTenantDefaults(): void {
        this.tenantRiskService.getCurrent().subscribe({
            next: (tenantRisk) => {
                this.tenantRisk = tenantRisk;
                if (!this.isCustomConfig) {
                    this.parseConfigurations(tenantRisk);
                }
                this.isLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        });
    }

    parseConfigurations(config: RiskConfigSource): void {
        this.censoredDataMethod = config.censoredDataMethod ?? CensoredDataMethod.LodOverSqrt2;

        if (config.riskLevels) {
            try {
                const parsed = JSON.parse(config.riskLevels) as Record<string, Partial<RiskLevelConfig>>;
                this.riskLevels = Object.entries(parsed).map(([key, value]) => ({
                    key,
                    Name: value.Name || key,
                    minThresholds: value.Thresholds?.[0] ?? 0,
                    maxThresholds: value.Thresholds?.[1] ?? 0,
                    Color: value.Color || DEFAULT_COLOR,
                    ReqMethodOfAssessment: value.ReqMethodOfAssessment ?? 0,
                    ControlAdequacy: value.ControlAdequacy ?? 1,
                    PostAssessmentControlAdequacy: value.PostAssessmentControlAdequacy ?? 1,
                    Description: value.Description || ''
                }));
            } catch (e) {
                this.riskLevels = [];
            }
        }

        if (config.exposureLikelihoodLevels) {
            try {
                const parsed = JSON.parse(config.exposureLikelihoodLevels) as ExposureLikelihoodLevelsStorage;
                // Extract and store ConsequenceRatings array if present
                this.consequenceRatings = (Array.isArray(parsed.ConsequenceRatings) && parsed.ConsequenceRatings.length > 0)
                    ? parsed.ConsequenceRatings
                    : [];
                // Filter out ConsequenceRatings array (not a level, just metadata)
                this.exposureLikelihoodLevels = Object.entries(parsed)
                    .filter(([key, value]) => key !== 'ConsequenceRatings' && typeof value === 'object' && value !== null && !Array.isArray(value))
                    .map(([key, value]) => {
                        const level = value as Partial<ExposureLikelihoodLevelConfig>;
                        return {
                            key,
                            Name: level.Name || key,
                            QuantitativeName: level.QuantitativeName?.trim() ?? '',
                            minQualThreshold: level.QualThresholds?.[0] ?? 0,
                            maxQualThreshold: level.QualThresholds?.[1] ?? 0,
                            minQuantThreshold: level.QuantThreshold_OEL?.[0] ?? 0,
                            maxQuantThreshold: level.QuantThreshold_OEL?.[1] ?? 0,
                            MinSamples: level.MinSamples ?? 6,
                            Color: level.Color || DEFAULT_COLOR,
                            ReassessmentFrequencyMonths: level.ReassessmentFrequencyMonths ?? 12,
                            Description: level.Description || ''
                        };
                    });
                this.loadOrDetectMatrixDimensions(config);
                this.proposedQualThresholds = [];
                this.showThresholdSuggestion = false;
                this.thresholdSuggestionPending = false;
            } catch (e) {
                this.exposureLikelihoodLevels = [];
                this.consequenceRatings = [];
            }
        }

        this.applyConsequenceMethodMapping(config.consequenceMethodMapping);

        this.parseQualitativeMatrixConfig(config);
        this.parseBayesianConfig(config);
        this.refreshBayesianQuantBandsReference();
        this.recomputeLegends();
        this.updateValidationState();
    }

    /**
     * Seeded consequence-rating -> required-method mapping (rating 5 => Quantitative).
     * Single source of truth for both the on-load default and the on-save fallback so the seed is
     * never wiped and the backend (which rejects a null `ConsequenceMethodMapping`) never receives null.
     */
    private readonly seededConsequenceMethodMapping: Record<string, number> = { '5': MethodOfAssessment.Quantitative };

    /** Valid `MethodOfAssessment` enum values; anything outside this set is treated as unmapped. */
    private static readonly validMethodOfAssessmentValues: ReadonlySet<number> = new Set([
        MethodOfAssessment.Qualitative,
        MethodOfAssessment.Quantitative,
        MethodOfAssessment.DoesNotApply,
    ]);

    /**
     * Hydrate each consequence rating's `reqMethodOfAssessment` from the config-driven
     * `consequenceMethodMapping` (JSON keyed by rating value). Ratings absent from the mapping default
     * to `DoesNotApply` (no consequence-driven requirement). When the source has no mapping (e.g. tenant
     * defaults, which don't carry the field), fall back to the seeded `{"5":1}` so nothing appears changed.
     */
    private applyConsequenceMethodMapping(mappingJson?: string): void {
        let mapping: Record<string, number> = { ...this.seededConsequenceMethodMapping };
        if (mappingJson) {
            try {
                const parsed = JSON.parse(mappingJson) as Record<string, number>;
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    mapping = parsed;
                }
            } catch {
                // Malformed mapping — silently keep the seeded default (the UI immediately repairs the field on save).
                // Deliberately not logged: console.* is disallowed in app code and would echo raw backend payload.
            }
        }
        this.consequenceRatings = this.consequenceRatings.map(rating => {
            const method = mapping[String(rating.value)];
            // Guard against corrupted/tampered values (e.g. {"5":99}) so they are never stored or round-tripped.
            const isValid = typeof method === 'number'
                && JurisdictionRiskConfigFormComponent.validMethodOfAssessmentValues.has(method);
            return {
                ...rating,
                reqMethodOfAssessment: isValid ? method : MethodOfAssessment.DoesNotApply
            };
        });
    }

    /**
     * DASH-2661: serialise the per-consequence-rating required methods into the
     * `consequenceMethodMapping` JSON the backend reads. `DoesNotApply` entries are omitted so the
     * neutral default keeps the stored mapping minimal (an unchanged rating 5 => Quantitative
     * round-trips back to `{"5":1}`).
     */
    private buildConsequenceMethodMapping(): string {
        const mapping = this.consequenceRatings.reduce((acc, rating) => {
            const method = rating.reqMethodOfAssessment ?? MethodOfAssessment.DoesNotApply;
            if (method !== MethodOfAssessment.DoesNotApply) {
                acc[String(rating.value)] = method;
            }
            return acc;
        }, {} as Record<string, number>);
        return JSON.stringify(mapping);
    }

    private parseBayesianConfig(config: RiskConfigSource): void {
        const defaults: BayesianConfig = {
            enabled: false,
            framework: 'Bayesian Exposure Assessment',
            defaultPriorMode: 0,
            defaultPriorWidth: 'wide',
            informativePriorSource: 'QRA',
            enableCensoringGuardrail: true,
            notes: '',
        };

        if (config.defaultPriorMode != null || config.defaultPriorWidth != null ||
            config.enableCensoringGuardrail != null || config.configurationNotes != null ||
            config.isBayesianAnalysisEnabled != null) {
            this.applyBayesianConfig({
                ...defaults,
                enabled: config.isBayesianAnalysisEnabled ?? defaults.enabled,
                isBayesianAnalysisEnabled: config.isBayesianAnalysisEnabled,
                defaultPriorMode: config.defaultPriorMode ?? defaults.defaultPriorMode,
                defaultPriorWidth: config.defaultPriorWidth ?? 0,
                enableCensoringGuardrail: config.enableCensoringGuardrail ?? defaults.enableCensoringGuardrail,
                configurationNotes: config.configurationNotes ?? '',
                notes: config.configurationNotes ?? '',
            });
            return;
        }

        if (!config.bayesianConfig) {
            this.applyBayesianConfig(defaults);
            return;
        }

        try {
            const parsed = JSON.parse(config.bayesianConfig) as BayesianConfig;
            this.applyBayesianConfig({
                ...defaults,
                ...parsed,
            });
        } catch {
            this.applyBayesianConfig(defaults);
        }
    }

    private applyBayesianConfig(config: BayesianConfig): void {
        this.bayesianEnabled = config.enabled ?? false;
        this.bayesianFramework = config.framework || 'Bayesian Exposure Assessment';
        this.bayesianDefaultPriorMode = this.bayesianInformativePriorDisabled
            ? 0
            : (config.defaultPriorMode ?? 0);
        const priorWidth = config.defaultPriorWidth;
        this.bayesianDefaultPriorWidth = this.bayesianNarrowPriorDisabled
            ? 'wide'
            : (priorWidth === 1 || priorWidth === 'narrow' ? 'narrow' : 'wide');
        this.bayesianInformativePriorSource = config.informativePriorSource || 'QRA';
        this.bayesianEnableCensoringGuardrail = config.enableCensoringGuardrail !== false;
        this.bayesianNotes = config.configurationNotes || config.notes || '';
    }

    private refreshBayesianQuantBandsReference(): void {
        this.bayesianQuantBandsReference = [...this.exposureLikelihoodLevels]
            .sort((a, b) => Number(a.key) - Number(b.key))
            .map((level) => ({
                name: level.QuantitativeName || level.Name,
                color: level.Color || DEFAULT_COLOR,
                range: `${level.minQuantThreshold}-${level.maxQuantThreshold}% OEL`,
            }));
    }

    saveConfiguration(): void {
        if (!this.canEditRiskConfig) return;

        this.isLoading = true;

        const exposureLikelihoodLevelsObj = this.exposureLikelihoodLevels.reduce((acc, item) => {
            acc[item.key] = {
                Name: item.Name,
                QuantitativeName: item.QuantitativeName || '',
                QualThresholds: [item.minQualThreshold, item.maxQualThreshold],
                QuantThreshold_OEL: [item.minQuantThreshold, item.maxQuantThreshold],
                MinSamples: item.MinSamples,
                Color: item.Color,
                ReassessmentFrequencyMonths: item.ReassessmentFrequencyMonths,
                ...(item.Description && { Description: item.Description })
            };
            return acc;
        }, {} as ExposureLikelihoodLevelsStorage);
        
        // Include ConsequenceRatings in the JSON if present. The per-rating required method of
        // assessment is persisted separately via consequenceMethodMapping (DASH-2661), so it is not
        // duplicated into this array.
        if (this.consequenceRatings.length > 0) {
            exposureLikelihoodLevelsObj.ConsequenceRatings = this.consequenceRatings.map(rating => ({
                value: rating.value,
                label: rating.label,
                description: rating.description
            }));
        }
        
        const exposureLikelihoodLevelsJson = JSON.stringify(exposureLikelihoodLevelsObj);

        const validationResult = !this.bayesianOnly
            ? this.jurisdictionRiskConfigService.validateQualThresholdRanges(
                  exposureLikelihoodLevelsJson,
                  this.maxQualitativeProduct
              )
            : { valid: true, errors: [] };

        if (!validationResult.valid) {
            this.isLoading = false;
            const errorMessage = validationResult.errors.join('\n');
            this.toastService.displayErrorMessage('Configuration validation failed', true);
            this.toastService.displayErrorMessage(errorMessage);
            return;
        }

        const likelihoodCoverageValidation = !this.bayesianOnly
            ? this.validateLikelihoodCoverageForConfiguredScores()
            : { valid: true, errors: [] };
        if (!likelihoodCoverageValidation.valid) {
            this.isLoading = false;
            this.toastService.displayErrorMessage('Configuration validation failed', true);
            this.toastService.displayErrorMessage(likelihoodCoverageValidation.errors.join('\n'));
            return;
        }

        const qualValidation = !this.bayesianOnly
            ? this.validateQualitativeConfig()
            : { valid: true, errors: [] };
        if (!qualValidation.valid) {
            this.isLoading = false;
            this.toastService.displayErrorMessage('Qualitative config validation failed', true);
            this.toastService.displayErrorMessage(qualValidation.errors.join('\n'));
            return;
        }

        // DASH-3163: per-band Min Samp is now an authoritative control (governs the required sample
        // count when EN 689 is disabled), so enforce the 1–1000 range matching the server-side rule.
        const minSampValidation = !this.bayesianOnly
            ? this.validateMinSamplesRange()
            : { valid: true, errors: [] };
        if (!minSampValidation.valid) {
            this.isLoading = false;
            this.toastService.displayErrorMessage('Configuration validation failed', true);
            this.toastService.displayErrorMessage(minSampValidation.errors.join('\n'));
            return;
        }

        const config: JurisdictionRiskConfig = {
            id: this.isCustomConfig ? this.jurisdictionConfig?.id : undefined,
            jurisdictionId: this.jurisdictionId,
            riskLevels: !this.bayesianOnly
                ? JSON.stringify(
                      this.riskLevels.reduce((acc, item) => {
                          acc[item.key] = {
                              Name: item.Name,
                              Thresholds: [item.minThresholds, item.maxThresholds],
                              Color: item.Color,
                              ReqMethodOfAssessment: item.ReqMethodOfAssessment,
                              ControlAdequacy: item.ControlAdequacy,
                              PostAssessmentControlAdequacy: item.PostAssessmentControlAdequacy,
                              ...(item.Description && { Description: item.Description })
                          };
                          return acc;
                      }, {} as Record<string, RiskLevelConfig>)
                  )
                : (this.jurisdictionConfig?.riskLevels || this.tenantRisk?.riskLevels || undefined),
            exposureLikelihoodLevels: exposureLikelihoodLevelsJson,
            matrixExceedanceDimension: this.matrixExceedanceDimension,
            matrixFrequencyDimension: this.matrixFrequencyDimension,
            qualitativeConfig: !this.bayesianOnly
                ? JSON.stringify(this.toApiQualitativeConfig())
                : (this.jurisdictionConfig?.qualitativeConfig || undefined),
            defaultPriorMode: this.bayesianDefaultPriorMode,
            defaultPriorWidth: this.bayesianDefaultPriorWidth === 'narrow' ? 1 : 0,
            enableCensoringGuardrail: this.bayesianEnableCensoringGuardrail,
            configurationNotes: this.bayesianNotes || undefined,
            isBayesianAnalysisEnabled: this.bayesianEnabled,
            useEN689: this.bayesianOnly ? (this.jurisdictionConfig?.useEN689 ?? null) : this.useEN689,
            censoredDataMethod: this.censoredDataMethod,
            // DASH-2661: persist the consequence-rating -> required-method mapping. When there are no
            // custom consequence ratings to serialise (or in the Bayesian-only view), preserve the
            // existing mapping; when none exists (e.g. a brand-new custom config), fall back to the
            // seeded default so the field is never wiped and never sent as null (the backend rejects a
            // null ConsequenceMethodMapping).
            consequenceMethodMapping: (!this.bayesianOnly && this.consequenceRatings.length > 0)
                ? this.buildConsequenceMethodMapping()
                : (this.jurisdictionConfig?.consequenceMethodMapping
                    ?? JSON.stringify(this.seededConsequenceMethodMapping)),
        };

        const serviceCall = this.isCustomConfig
            ? this.jurisdictionRiskConfigService.update(config)
            : this.jurisdictionRiskConfigService.create(config);

        serviceCall.subscribe({
            next: () => {
                this.toastService.showSuccess('Configuration saved successfully', true);
                this.isCustomConfig = true;
                this.hasChanges = false;
                this.isLoading = false;
                this.loadConfigurations();
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        });
    }

    deleteConfiguration(): void {
        if (!this.canEditRiskConfig || !this.isCustomConfig) return;

        this.isLoading = true;
        this.jurisdictionRiskConfigService.delete(this.jurisdictionConfig?.id).subscribe({
            next: () => {
                this.toastService.showSuccess('Reset to tenant defaults', true);
                this.isCustomConfig = false;
                this.loadConfigurations();
                this.hasChanges = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        });
    }

    createFromDefaults(): void {
        this.parseConfigurations(this.tenantRisk);
        this.hasChanges = true;
    }

    onConfigChanges(): void {
        this.hasChanges = true;
        this.refreshBayesianQuantBandsReference();
        this.refreshMatrixPreviewCache();
        this.updateValidationState();
    }

    onBayesianDefaultPriorModeChange(mode: number): void {
        this.bayesianDefaultPriorMode = mode;
        this.onConfigChanges();
    }

    discardChanges(): void {
        if (this.isCustomConfig && this.jurisdictionConfig) {
            this.parseConfigurations(this.jurisdictionConfig);
            this.useEN689 = this.jurisdictionConfig.useEN689 ?? true;
        } else if (this.tenantRisk) {
            this.parseConfigurations(this.tenantRisk);
            // No custom jurisdiction override exists here, so the EN 689 toggle falls back to
            // the backend default (true). useEN689 lives on JurisdictionRiskConfig, not TenantRisk.
            this.useEN689 = true;
        }
        this.hasChanges = false;
    }

    addRiskLevel(): void {
        const keys = this.riskLevels.map(r => Number(r.key));
        const newKey = keys.length > 0 ? String(Math.max(...keys) + 1) : '0';
        const lastLevel = this.riskLevels[this.riskLevels.length - 1];
        const newMin = lastLevel ? lastLevel.maxThresholds + 1 : 0;

        this.riskLevels.push({
            key: newKey,
            Name: this.translateService.getDynamicTranslation('New Level'),
            minThresholds: newMin,
            maxThresholds: newMin + 2,
            Color: DEFAULT_NEW_LEVEL_COLOR,
            ReqMethodOfAssessment: 0,
            ControlAdequacy: 1,
            PostAssessmentControlAdequacy: 1
        });
        this.hasChanges = true;
        this.updateValidationState();
    }

    deleteRiskLevel(index: number): void {
        if (this.riskLevels.length <= 1) return;
        this.riskLevels.splice(index, 1);
        this.hasChanges = true;
        this.updateValidationState();
    }

    updateRiskLevelKey(index: number, inputEl: HTMLInputElement): void {
        const newKey = parseInt(inputEl.value, 10);
        if (isNaN(newKey) || newKey < 1) {
            this.toastService.displayErrorMessage('Key must be a positive integer', true);
            inputEl.value = this.riskLevels[index].key;
            return;
        }
        const duplicate = this.riskLevels.some((l, i) => i !== index && Number(l.key) === newKey);
        if (duplicate) {
            this.toastService.displayErrorMessage('Key {{key}} already exists. Keys must be unique.', true, { key: newKey });
            inputEl.value = this.riskLevels[index].key;
            return;
        }
        this.riskLevels[index].key = String(newKey);
        this.onConfigChanges();
    }

    updateExposureLevelKey(index: number, inputEl: HTMLInputElement): void {
        const newKey = parseInt(inputEl.value, 10);
        if (isNaN(newKey) || newKey < 1) {
            this.toastService.displayErrorMessage('Key must be a positive integer', true);
            inputEl.value = this.exposureLikelihoodLevels[index].key;
            return;
        }
        const duplicate = this.exposureLikelihoodLevels.some((l, i) => i !== index && Number(l.key) === newKey);
        if (duplicate) {
            this.toastService.displayErrorMessage('Key {{key}} already exists. Keys must be unique.', true, { key: newKey });
            inputEl.value = this.exposureLikelihoodLevels[index].key;
            return;
        }
        this.exposureLikelihoodLevels[index].key = String(newKey);
        this.onConfigChanges();
    }

    addExposureLevel(): void {
        const keys = this.exposureLikelihoodLevels.map(l => Number(l.key));
        const newKey = keys.length > 0 ? String(Math.max(...keys) + 1) : '1';
        const lastLevel = this.exposureLikelihoodLevels[this.exposureLikelihoodLevels.length - 1];

        this.exposureLikelihoodLevels.push({
            key: newKey,
            Name: this.translateService.getDynamicTranslation('New Level'),
            QuantitativeName: '',
            minQualThreshold: lastLevel ? lastLevel.maxQualThreshold : 0,
            maxQualThreshold: lastLevel ? lastLevel.maxQualThreshold + 5 : 5,
            minQuantThreshold: lastLevel ? lastLevel.maxQuantThreshold : 0,
            maxQuantThreshold: lastLevel ? lastLevel.maxQuantThreshold + 50 : 50,
            MinSamples: 6,
            Color: DEFAULT_NEW_LIKELIHOOD_COLOR,
            ReassessmentFrequencyMonths: 12
        });
        this.hasChanges = true;
        this.updateValidationState();
        this.dismissThresholdSuggestion();
    }

    deleteExposureLevel(index: number): void {
        if (this.exposureLikelihoodLevels.length <= 1) return;
        this.exposureLikelihoodLevels.splice(index, 1);
        this.hasChanges = true;
        this.updateValidationState();
        this.dismissThresholdSuggestion();
    }

    getColorHex(color: string): string {
        return color?.substring(0, 7) || DEFAULT_COLOR;
    }

    onColorSwatchChange(level: RiskLevelItem, hexVal: string): void {
        const alpha = level.Color.length > 7 ? level.Color.substring(7) : '';
        level.Color = hexVal + alpha;
        this.hasChanges = true;
    }

    onColorHexChange(level: RiskLevelItem, hexVal: string): void {
        level.Color = hexVal;
        this.hasChanges = true;
    }

    onLikelihoodColorSwatchChange(level: ExposureLikelihoodLevelItem, hexVal: string): void {
        level.Color = hexVal;
        this.hasChanges = true;
    }

    onLikelihoodColorHexChange(level: ExposureLikelihoodLevelItem, hexVal: string): void {
        level.Color = hexVal;
        this.hasChanges = true;
    }

    toggleRiskLevelControlAdequacy(level: RiskLevelItem, checked: boolean): void {
        level.ControlAdequacy = checked ? 1 : 0;
        this.onConfigChanges();
    }

    toggleRiskLevelPostAssessmentControlAdequacy(level: RiskLevelItem, checked: boolean): void {
        level.PostAssessmentControlAdequacy = checked ? 1 : 0;
        this.onConfigChanges();
    }

    getRiskScoreColor(score: number): string {
        for (const level of this.riskLevels) {
            if (score >= level.minThresholds && score <= level.maxThresholds) {
                return level.Color;
            }
        }
        return UNMATCHED_SCORE_COLOR;
    }

    getRiskPreviewSegments(): { name: string; color: string; widthPct: number; range: string }[] {
        if (!this.riskLevels.length) return [];
        const allMin = Math.min(...this.riskLevels.map(r => r.minThresholds));
        const allMax = Math.max(...this.riskLevels.map(r => r.maxThresholds));
        const totalRange = allMax - allMin + 1;
        if (totalRange <= 0) return [];

        return this.riskLevels.map(r => ({
            name: r.Name,
            color: r.Color,
            widthPct: ((r.maxThresholds - r.minThresholds + 1) / totalRange) * 100,
            range: `${r.minThresholds}–${r.maxThresholds}`
        }));
    }

    getLikelihoodQualPreviewSegments(): { name: string; color: string; widthPct: number; range: string }[] {
        if (!this.exposureLikelihoodLevels.length) return [];
        const totalRange = this.maxQualitativeProduct;
        return this.exposureLikelihoodLevels.map(l => ({
            name: l.Name,
            color: l.Color,
            widthPct: ((l.maxQualThreshold - l.minQualThreshold + 1) / totalRange) * 100,
            range: `${l.minQualThreshold}–${l.maxQualThreshold}`
        }));
    }

    getLikelihoodQuantPreviewSegments(): { name: string; color: string; widthPct: number; range: string }[] {
        if (!this.exposureLikelihoodLevels.length) return [];
        const displayMax = 200;
        return this.exposureLikelihoodLevels.map(l => {
            const min = l.minQuantThreshold;
            const max = Math.min(l.maxQuantThreshold, displayMax);
            const span = max - min;
            return {
                name: l.Name,
                color: l.Color,
                widthPct: span > 0 ? (span / displayMax) * 100 : 0,
                range: `${l.minQuantThreshold}–${l.maxQuantThreshold}%`
            };
        });
    }

    getOelMarkerPosition(): number {
        return (100 / 200) * 100; // 100% OEL on a 0-200 display scale
    }

    get likelihoodCount(): number {
        return this.exposureLikelihoodLevels.length || 5;
    }

    get consequenceCount(): number {
        // DASH-2278: the consequence axis is driven by the configured Consequence
        // Ratings (independent of the likelihood-level count), mirroring the risk
        // register matrix where columns are the actual consequence ratings.
        return this.consequenceRatings.length || 5;
    }

    get maxRiskScore(): number {
        // The true maximum Health Risk Score is the highest likelihood factor
        // multiplied by the highest consequence rating value (values are
        // configurable and need not be a contiguous 1..N range).
        const maxLikelihood = this.exposureLikelihoodLevels.length
            ? Math.max(...this.exposureLikelihoodLevels.map(l => Number(l.key)))
            : this.likelihoodCount;
        const maxConsequence = this.consequenceRatings.length
            ? Math.max(...this.consequenceRatings.map(r => r.value))
            : this.consequenceCount;
        return maxLikelihood * maxConsequence;
    }

    // DASH-2278: likelihood/consequence values are configurable and need not be a
    // contiguous 1..N range, so the matrix bounds derive from the actual min/max
    // values rather than assuming a 1-based range.
    get minLikelihoodValue(): number {
        return this.exposureLikelihoodLevels.length
            ? Math.min(...this.exposureLikelihoodLevels.map(l => Number(l.key)))
            : 1;
    }

    get maxLikelihoodValue(): number {
        return this.exposureLikelihoodLevels.length
            ? Math.max(...this.exposureLikelihoodLevels.map(l => Number(l.key)))
            : this.likelihoodCount;
    }

    get minConsequenceValue(): number {
        return this.consequenceRatings.length
            ? Math.min(...this.consequenceRatings.map(r => r.value))
            : 1;
    }

    get maxConsequenceValue(): number {
        return this.consequenceRatings.length
            ? Math.max(...this.consequenceRatings.map(r => r.value))
            : this.consequenceCount;
    }

    get minRiskScore(): number {
        return this.minLikelihoodValue * this.minConsequenceValue;
    }

    // DASH-2278: build the risk matrix exactly like the risk-register popup —
    // likelihood factors down the rows, consequence ratings across the columns,
    // each cell = likelihoodFactor × consequenceValue coloured by its risk band.
    getRiskMatrix(): { likelihoodLabel: string; cells: { score: number; color: string }[] }[] {
        const consequences = this.getConsequenceAxis();
        return this.getLikelihoodAxis().map(likelihood => ({
            likelihoodLabel: likelihood.label,
            cells: consequences.map(consequence => {
                const score = likelihood.key * consequence.value;
                return { score, color: this.getRiskScoreColor(score) };
            })
        }));
    }

    // Likelihood factors, highest first (matches the risk-register row order).
    getLikelihoodAxis(): { key: number; label: string }[] {
        if (this.exposureLikelihoodLevels.length) {
            return this.exposureLikelihoodLevels
                .map(l => ({ key: Number(l.key), label: `${Number(l.key)} ${l.Name}` }))
                .sort((a, b) => b.key - a.key);
        }
        return Array.from({ length: this.likelihoodCount }, (_, i) => {
            const key = this.likelihoodCount - i;
            return { key, label: `${key}` };
        });
    }

    // Consequence ratings, lowest value first.
    getConsequenceAxis(): { value: number; label: string }[] {
        if (this.consequenceRatings.length) {
            return this.consequenceRatings
                .map(r => ({ value: r.value, label: r.label ? `${r.value} - ${r.label}` : `${r.value}` }))
                .sort((a, b) => a.value - b.value);
        }
        return Array.from({ length: this.consequenceCount }, (_, i) => ({ value: i + 1, label: `${i + 1}` }));
    }

    initializeDefaultQualitativeConfig(): QualitativeMatrixConfig {
        return {
            matrixSize: 5,
            isEnabled: true,
            dimensions: [
                {
                    id: 'dimension_1',
                    name: 'Exceedance Probability',
                    options: [
                        { value: 1, label: '1 - Negligible' },
                        { value: 2, label: '2 - Unlikely' },
                        { value: 3, label: '3 - Possible' },
                        { value: 4, label: '4 - Likely' },
                        { value: 5, label: '5 - Continuously' }
                    ]
                },
                {
                    id: 'dimension_2',
                    name: 'Frequency of Exposure',
                    options: [
                        { value: 1, label: '1 - <20% shifts' },
                        { value: 2, label: '2 - 20-39%' },
                        { value: 3, label: '3 - 40-59%' },
                        { value: 4, label: '4 - 60-79%' },
                        { value: 5, label: '5 - 80-100%' }
                    ]
                }
            ],
            likelihoodMappings: [
                { minProductScore: 1, maxProductScore: 4, likelihoodScore: 1, likelihoodLabel: 'Very Low', color: '#16A34A' },
                { minProductScore: 5, maxProductScore: 8, likelihoodScore: 2, likelihoodLabel: 'Low', color: '#84CC16' },
                { minProductScore: 9, maxProductScore: 12, likelihoodScore: 3, likelihoodLabel: 'Medium', color: '#FFB800' },
                { minProductScore: 13, maxProductScore: 19, likelihoodScore: 4, likelihoodLabel: 'High', color: '#F97316' },
                { minProductScore: 20, maxProductScore: 25, likelihoodScore: 5, likelihoodLabel: 'Very High', color: '#DC2626' }
            ]
        };
    }

    parseQualitativeMatrixConfig(config: RiskConfigSource): void {
        const defaults = this.initializeDefaultQualitativeConfig();

        if (config.qualitativeConfig) {
            try {
                const parsed = typeof config.qualitativeConfig === 'string'
                    ? JSON.parse(config.qualitativeConfig)
                    : config.qualitativeConfig;

                // Support matrix-shaped payloads when qualitativeConfig is used as the single storage field.
                if (Array.isArray(parsed?.dimensions) && Array.isArray(parsed?.likelihoodMappings)) {
                    this.qualitativeMatrixConfig = {
                        ...defaults,
                        ...parsed,
                        dimensions: parsed.dimensions.length === 2 ? parsed.dimensions : defaults.dimensions,
                        likelihoodMappings: parsed.likelihoodMappings.length ? parsed.likelihoodMappings : defaults.likelihoodMappings
                    };
                    this.refreshMatrixPreviewCache();
                    return;
                }

                if (Array.isArray(parsed?.dimensions) && Array.isArray(parsed?.likelihoodMapping)) {
                    this.qualitativeMatrixConfig = this.fromApiQualitativeConfig(parsed as QualitativeConfigApi);
                    this.refreshMatrixPreviewCache();
                    return;
                }
            } catch (e) {
                this.qualitativeMatrixConfig = defaults;
                this.refreshMatrixPreviewCache();
                return;
            }
        }

        this.qualitativeMatrixConfig = defaults;
        this.refreshMatrixPreviewCache();
    }

    get maxQualitativeProduct(): number {
        return this.matrixExceedanceDimension * this.matrixFrequencyDimension;
    }

    loadOrDetectMatrixDimensions(config: RiskConfigSource): void {
        if (config.matrixExceedanceDimension && config.matrixFrequencyDimension &&
            config.matrixExceedanceDimension > 1 && config.matrixFrequencyDimension > 1) {
            this.matrixExceedanceDimension = config.matrixExceedanceDimension;
            this.matrixFrequencyDimension = config.matrixFrequencyDimension;
            this._prevMaxProduct = config.matrixExceedanceDimension * config.matrixFrequencyDimension;
            return;
        }

        if (!this.exposureLikelihoodLevels.length) return;

        const maxThreshold = Math.max(...this.exposureLikelihoodLevels.map(l => l.maxQualThreshold));
        const dimensions = Math.sqrt(maxThreshold);
        if (Number.isInteger(dimensions) && dimensions > 1) {
            this.matrixExceedanceDimension = dimensions;
            this.matrixFrequencyDimension = dimensions;
        }

        this._prevMaxProduct = this.matrixExceedanceDimension * this.matrixFrequencyDimension
    }

    calculateSuggestedQualThresholds(): SuggestedQualThreshold[] {
        const maxProduct = this.maxQualitativeProduct;
        const levelCount = this.exposureLikelihoodLevels.length;

        if (levelCount === 0 || maxProduct < 1) return [];
        if (levelCount > maxProduct) {
            this.toastService.displayErrorMessage(
                'Matrix size is too small for the current number of likelihood levels',
                true,
            );
            return [];
        }

        const suggested: SuggestedQualThreshold[] = [];

        for (let i = 0; i < levelCount; i++) {
            const level = this.exposureLikelihoodLevels[i];
            const suggestedMin = Math.floor((i * maxProduct) / levelCount) + 1;
            const suggestedMax = Math.floor(((i + 1) * maxProduct) / levelCount);

            suggested.push({
                levelIndex: i,
                levelName: level.Name,
                currentMin: level.minQualThreshold,
                currentMax: level.maxQualThreshold,
                suggestedMin,
                suggestedMax,
                color: level.Color
            });
        }

        return suggested;
    }

    private recomputeDimensionRequiredCounts(): void {
        this.dimensionRequiredCounts = [this.matrixExceedanceDimension, this.matrixFrequencyDimension];
    }

    onMatrixSizeChange(): void {
        this.syncDimensionOptionsToMatrixSize();
        this.qualitativeMatrixConfig.matrixSize = Math.max(
            this.matrixExceedanceDimension,
            this.matrixFrequencyDimension
        );
        this.onConfigChanges();
        const newMaxProduct = this.maxQualitativeProduct;

        if (newMaxProduct === this._prevMaxProduct) {
            this.showThresholdSuggestion = false;
            this.thresholdSuggestionPending = false;
            this.proposedQualThresholds = [];
            return;
        }

        this._prevMaxProduct = this.matrixExceedanceDimension * this.matrixFrequencyDimension;
        this.proposedQualThresholds = this.calculateSuggestedQualThresholds();
        const hasSuggestions = this.proposedQualThresholds.length > 0;
        this.showThresholdSuggestion = hasSuggestions;
        this.thresholdSuggestionPending = hasSuggestions;
    }

    applySuggestedThresholds(): void {
        if (!this.proposedQualThresholds.length) return;

        for (const suggestion of this.proposedQualThresholds) {
            const level = this.exposureLikelihoodLevels[suggestion.levelIndex];
            level.minQualThreshold = suggestion.suggestedMin;
            level.maxQualThreshold = suggestion.suggestedMax;
        }

        this.showThresholdSuggestion = false;
        this.thresholdSuggestionPending = false;
        this.hasChanges = true;
        this.onConfigChanges();
    }

    dismissThresholdSuggestion(): void {
        this.showThresholdSuggestion = false;
        this.thresholdSuggestionPending = false;
        this.proposedQualThresholds = [];
    }

    getDimensionMaxOptions(dimensionId: string): number {
        const index = this.qualitativeMatrixConfig.dimensions.findIndex(d => d.id === dimensionId);
        if (index === -1) {
            return 0; // unknown dimension — disable adding options rather than silently defaulting
        }
        return index === 0 ? this.matrixExceedanceDimension : this.matrixFrequencyDimension;
    }

    private syncDimensionOptionsToMatrixSize(): void {
        let changed = false;
        this.qualitativeMatrixConfig.dimensions.forEach(dimension => {
            const required = this.getDimensionMaxOptions(dimension.id);
            while (dimension.options.length < required) {
                const newValue = dimension.options.length
                    ? Math.max(...dimension.options.map(o => o.value)) + 1
                    : 1;
                dimension.options.push({ value: newValue, label: `${newValue}` });
                changed = true;
            }
            while (dimension.options.length > required) {
                dimension.options.pop();
                changed = true;
            }
        });
        if (changed) {
            this.regenerateLikelihoodMappings();
        }
    }

    removeDimensionOption(dimensionId: string, optionValue: number): void {
        const dimension = this.qualitativeMatrixConfig.dimensions.find(d => d.id === dimensionId);
        if (dimension && dimension.options.length > 2) { // Ensure at least 2 options
            const index = dimension.options.findIndex(o => o.value === optionValue);
            if (index > -1) {
                dimension.options.splice(index, 1);
                this.regenerateLikelihoodMappings();
                this.hasChanges = true;
                this.updateValidationState();
            }
        }
    }

    updateDimensionOptionValue(dimensionId: string, oldValue: number, newValue: number): void {
        const dimension = this.qualitativeMatrixConfig.dimensions.find(d => d.id === dimensionId);
        if (dimension && newValue > 0) {
            const option = dimension.options.find(o => o.value === oldValue);
            if (option) {
                // Check if new value already exists in this dimension
                if (dimension.options.some(o => o.value === newValue && o !== option)) {
                    this.toastService.displayErrorMessage(
                        this.translateService.getDynamicTranslation('Value already exists in this dimension', { value: newValue }),
                        false
                    );
                    return;
                }
                option.value = newValue;
                this.regenerateLikelihoodMappings();
                this.hasChanges = true;
            }
        } else if (newValue <= 0) {
            this.toastService.displayErrorMessage('Option value must be greater than 0', true);
        }
    }

    private getConfiguredQualitativeProducts(): number[] {
        const products: Set<number> = new Set();
        const dim0Values = this.qualitativeMatrixConfig.dimensions[0]?.options.map(o => o.value) || [];
        const dim1Values = this.qualitativeMatrixConfig.dimensions[1]?.options.map(o => o.value) || [];

        for (const v0 of dim0Values) {
            for (const v1 of dim1Values) {
                products.add(v0 * v1);
            }
        }

        return Array.from(products).sort((a, b) => a - b);
    }

    /**
     * DASH-3163: the per-band Min Samp column is the authoritative required-sample-count control for
     * jurisdictions with EN 689 disabled. Every band's value must be an integer in 1–1000, matching
     * the server-side validation, so the save is blocked when any value falls outside that range.
     */
    validateMinSamplesRange(): { valid: boolean; errors: string[] } {
        const errors: string[] = [];

        this.exposureLikelihoodLevels.forEach((level) => {
            const value = level.MinSamples;
            if (value == null || !Number.isInteger(value) || value < 1 || value > 1000) {
                errors.push(
                    this.translateService.getDynamicTranslation(
                        'Min Samp for "{{name}}" must be a whole number between 1 and 1000.',
                        { name: level.Name || level.key },
                    ),
                );
            }
        });

        return { valid: errors.length === 0, errors };
    }

    validateLikelihoodCoverageForConfiguredScores(): { valid: boolean; errors: string[] } {
        const errors: string[] = [];

        if (!this.exposureLikelihoodLevels || this.exposureLikelihoodLevels.length === 0) {
            errors.push(this.translateService.getDynamicTranslation('No likelihood levels are configured for this jurisdiction.'));
            return { valid: false, errors };
        }

        const configuredProducts = this.getConfiguredQualitativeProducts();

        for (const score of configuredProducts) {
            const isCovered = this.exposureLikelihoodLevels.some(
                level => score >= level.minQualThreshold && score <= level.maxQualThreshold
            );

            if (!isCovered) {
                errors.push(
                    this.translateService.getDynamicTranslation(
                        'Likelihood Levels are not configured to cover the computed score of {{score}} (exceedance x frequency score). Please update the Likelihood Range for a relevant score to be set.',
                        { score }
                    )
                );
            }
        }

        return { valid: errors.length === 0, errors };
    }

    validateQualitativeConfig(): { valid: boolean; errors: string[] } {
        const errors: string[] = [];

        // Validate dimensions
        if (!this.qualitativeMatrixConfig.dimensions || this.qualitativeMatrixConfig.dimensions.length !== 2) {
            errors.push(this.translateService.getDynamicTranslation('Must have exactly 2 dimensions'));
            return { valid: false, errors };
        }

        this.qualitativeMatrixConfig.dimensions.forEach(dim => {
            const required = this.getDimensionMaxOptions(dim.id);
            if (dim.options.length !== required) {
                errors.push(this.translateService.getDynamicTranslation(
                    `${dim.name} must have exactly ${required} options to match the configured ${this.matrixExceedanceDimension}×${this.matrixFrequencyDimension} matrix size (currently ${dim.options.length})`
                ));
            }
            if (!dim.name || dim.name.trim().length === 0) {
                errors.push(this.translateService.getDynamicTranslation('All dimensions must have a name'));
            }
        });

        // Validate likelihood mappings
        if (!this.qualitativeMatrixConfig.likelihoodMappings || this.qualitativeMatrixConfig.likelihoodMappings.length === 0) {
            errors.push(this.translateService.getDynamicTranslation('Must have at least one likelihood mapping'));
        }

        // Check for contiguous and non-overlapping ranges
        const sortedMappings = [...this.qualitativeMatrixConfig.likelihoodMappings]
            .sort((a, b) => a.minProductScore - b.minProductScore);

        const minProduct = Math.min(...this.qualitativeMatrixConfig.dimensions[0].options.map(o => o.value)) *
                          Math.min(...this.qualitativeMatrixConfig.dimensions[1].options.map(o => o.value));
        let expectedMin = minProduct;
        const maxProduct = Math.max(...this.qualitativeMatrixConfig.dimensions[0].options.map(o => o.value)) *
                          Math.max(...this.qualitativeMatrixConfig.dimensions[1].options.map(o => o.value));

        for (let i = 0; i < sortedMappings.length; i++) {
            const mapping = sortedMappings[i];

            if (mapping.minProductScore !== expectedMin) {
                errors.push(this.translateService.getDynamicTranslation(
                    `Mapping ${i + 1}: Expected min ${expectedMin}, got ${mapping.minProductScore}`
                ));
            }

            if (mapping.maxProductScore < mapping.minProductScore) {
                errors.push(this.translateService.getDynamicTranslation(
                    `Mapping ${i + 1}: Max score must be >= min score`
                ));
            }

            expectedMin = mapping.maxProductScore + 1;
        }

        // Check if all product scores are covered
        if (expectedMin <= maxProduct) {
            errors.push(this.translateService.getDynamicTranslation(
                `Not all product scores covered. Missing coverage up to ${maxProduct}`
            ));
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }

    regenerateLikelihoodMappings(): void {
        const oldMappings = this.qualitativeMatrixConfig.likelihoodMappings || [];
        const minProduct = Math.min(...this.qualitativeMatrixConfig.dimensions[0].options.map(o => o.value)) *
                          Math.min(...this.qualitativeMatrixConfig.dimensions[1].options.map(o => o.value));
        const maxProduct = Math.max(...this.qualitativeMatrixConfig.dimensions[0].options.map(o => o.value)) *
                          Math.max(...this.qualitativeMatrixConfig.dimensions[1].options.map(o => o.value));

        // Try to preserve existing mapping structure when possible
        const newMappings: LikelihoodMapping[] = [];
        const totalRange = maxProduct - minProduct + 1;
        const rangePerLevel = Math.ceil(totalRange / oldMappings.length);

        for (let i = 0; i < oldMappings.length; i++) {
            const minScore = minProduct + i * rangePerLevel;
            const maxScore = Math.min(minProduct + (i + 1) * rangePerLevel - 1, maxProduct);

            newMappings.push({
                minProductScore: minScore,
                maxProductScore: maxScore,
                likelihoodScore: oldMappings[i].likelihoodScore || i + 1,
                likelihoodLabel: oldMappings[i].likelihoodLabel || `Level ${i + 1}`,
                color: oldMappings[i].color || DEFAULT_NEW_LIKELIHOOD_COLOR
            });
        }

        this.qualitativeMatrixConfig.likelihoodMappings = newMappings;

        // Refresh caches after configuration changes
        this.refreshMatrixPreviewCache();
    }

    private refreshMatrixPreviewCache(): void {
        const dim1 = this.qualitativeMatrixConfig.dimensions[0];
        const dim2 = this.qualitativeMatrixConfig.dimensions[1];
        const matrix: { product: number; color: string; mapping: LikelihoodMapping | null }[][] = [];

        // Use actual configured option values, sorted high→low for dim1, low→high for dim2
        const dim1Values = [...dim1.options].sort((a, b) => b.value - a.value).map(o => o.value);
        const dim2Values = [...dim2.options].sort((a, b) => a.value - b.value).map(o => o.value);

        // Create matrix from highest to lowest for dim1
        for (const i of dim1Values) {
            const row: { product: number; color: string; mapping: LikelihoodMapping | null }[] = [];
            for (const j of dim2Values) {
                const product = i * j;
                const mapping = this.qualitativeMatrixConfig.likelihoodMappings.find(
                    m => product >= m.minProductScore && product <= m.maxProductScore
                );

                // Derive color from exposure likelihood level qual thresholds for consistency
                const exposureLevel = this.exposureLikelihoodLevels.find(
                    l => product >= l.minQualThreshold && product <= l.maxQualThreshold
                );
                const color = exposureLevel?.Color || mapping?.color || UNMATCHED_SCORE_COLOR;

                row.push({
                    product,
                    color,
                    mapping: mapping || null
                });
            }
            matrix.push(row);
        }
        this.matrixPreviewCache = matrix;

        // Refresh axis label caches
        this.axisLabels0Cache = this.computeAxisLabels(dim1.options, 0);
        this.axisLabels1Cache = this.computeAxisLabels(dim2.options, 1);
        this.consequenceAxisCache = this.getConsequenceAxis();
        this.riskMatrixCache = this.getRiskMatrix();
        this.recomputeLegends();
    }

    /**
     * DASH-3171: rebuild the legend item arrays consumed by the shared <app-risk-config-legend>. Kept in
     * a cache field (never a template method call) so the same legend markup is shared with the Risk
     * Assessment page and the two pages cannot drift.
     */
    private recomputeLegends(): void {
        this.likelihoodQualLegendItems = this.exposureLikelihoodLevels.map(level => ({
            color: level.Color,
            name: (level.Name ?? '').trim(),
            range: `${level.minQualThreshold}–${level.maxQualThreshold}`
        }));

        this.likelihoodQuantLegendItems = this.exposureLikelihoodLevels.map(level => ({
            color: level.Color,
            name: (level.Name ?? '').trim(),
            range: `${level.minQuantThreshold}–${level.maxQuantThreshold}%`
        }));

        this.riskLevelLegendItems = this.riskLevels.map(level => ({
            color: level.Color,
            name: (level.Name ?? '').trim(),
            range: `${level.minThresholds}–${level.maxThresholds}`
        }));
    }

    getQualitativeMatrixPreview(): { product: number; color: string; mapping: LikelihoodMapping | null }[][] {
        return this.matrixPreviewCache;
    }

    getAxisLabels(options: QualitativeDimensionOption[] | undefined, dimension: number): QualitativeDimensionOption[] {
        return dimension === 0 ? this.axisLabels0Cache : this.axisLabels1Cache;
    }

    private computeAxisLabels(options: QualitativeDimensionOption[] | undefined, dimension: number): QualitativeDimensionOption[] {
        if (!options) return [];

        // Sort options the same way as the matrix preview
        let sorted: QualitativeDimensionOption[];
        if (dimension === 0) {
            // Dimension 0 (vertical axis): high → low
            sorted = [...options].sort((a, b) => b.value - a.value);
        } else {
            // Dimension 1 (horizontal axis): low → high
            sorted = [...options].sort((a, b) => a.value - b.value);
        }

        // Limit preview labels to matrix size to keep the axis readable.
        // Using matrixSize (not a hardcoded constant) prevents silent truncation
        // if matrix support expands beyond the current 3–6 range.
        const maxItems = Math.min(sorted.length, this.qualitativeMatrixConfig.matrixSize);
        return sorted.slice(0, maxItems);
    }

    getMatrixCellTooltip(cell: { product: number; color: string; mapping: LikelihoodMapping | null }): string {
        const productLabel = this.translateService.getDynamicTranslation('Product');
        const unmappedLabel = this.translateService.getDynamicTranslation('Unmapped');
        const likelihood = cell.mapping?.likelihoodLabel || unmappedLabel;
        return `${productLabel}: ${cell.product} → ${likelihood}`;
    }

    private toApiQualitativeConfig(): QualitativeConfigApi {
        return {
            dimensions: this.qualitativeMatrixConfig.dimensions.map(dim => ({
                name: dim.name,
                ...(dim.description && { description: dim.description }),
                options: dim.options.map(o => ({
                    value: o.value,
                    label: o.label,
                    ...(o.description && { description: o.description })
                }))
            })),
            likelihoodMapping: this.qualitativeMatrixConfig.likelihoodMappings.map(m => ({
                minProduct: m.minProductScore,
                maxProduct: m.maxProductScore,
                likelihoodScore: m.likelihoodScore,
                classification: m.likelihoodLabel,
                ...(m.description && { description: m.description })
            }))
        };
    }

    private fromApiQualitativeConfig(api: QualitativeConfigApi): QualitativeMatrixConfig {
        const defaults = this.initializeDefaultQualitativeConfig();
        const defaultColors = defaults.likelihoodMappings.map(m => m.color);
        const dim1 = api.dimensions[0] ?? defaults.dimensions[0];
        const dim2 = api.dimensions[1] ?? defaults.dimensions[1];

        return {
            matrixSize: Math.max(dim1.options.length, dim2.options.length) || defaults.matrixSize,
            isEnabled: true,
            dimensions: [
                {
                    id: 'dimension_1',
                    name: dim1.name,
                    description: dim1.description,
                    options: dim1.options.map(o => ({
                        value: o.value,
                        label: o.label,
                        description: o.description
                    }))
                },
                {
                    id: 'dimension_2',
                    name: dim2.name,
                    description: dim2.description,
                    options: dim2.options.map(o => ({
                        value: o.value,
                        label: o.label,
                        description: o.description
                    }))
                }
            ],
            likelihoodMappings: api.likelihoodMapping.map((m, i) => ({
                minProductScore: m.minProduct,
                maxProductScore: m.maxProduct,
                likelihoodScore: m.likelihoodScore,
                likelihoodLabel: m.classification,
                description: m.description,
                color: this.exposureLikelihoodLevels[i]?.Color ?? defaultColors[i] ?? DEFAULT_NEW_LIKELIHOOD_COLOR
            }))
        };
    }

    scrollToSection(sectionId: string): void {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    togglePipelineHIW(): void {
        this.showPipelineHIW = !this.showPipelineHIW;
    }

    hasDimensionMismatch: boolean = false;
    hasThresholdCoverageGap: boolean = false;
    hasQualitativeOptionCountMismatch: boolean = false;

    private updateValidationState(): void {
        this.recomputeDimensionRequiredCounts();
        this.hasQualitativeOptionCountMismatch = (this.qualitativeMatrixConfig?.dimensions || [])
            .some(dim => dim.options.length !== this.getDimensionMaxOptions(dim.id));

        this.hasDimensionMismatch = this.exposureLikelihoodLevels.length > 0 &&
            this.riskLevels.length > 0 &&
            this.exposureLikelihoodLevels.length !== this.riskLevels.length;

        if (!this.riskLevels.length) {
            this.hasThresholdCoverageGap = false;
            return;
        }
        const sorted = [...this.riskLevels].sort((a, b) => a.minThresholds - b.minThresholds);
        if (sorted[0].minThresholds !== 1) {
            this.hasThresholdCoverageGap = true;
            return;
        }
        for (let i = 1; i < sorted.length; i++) {
            if (sorted[i].minThresholds !== sorted[i - 1].maxThresholds + 1) {
                this.hasThresholdCoverageGap = true;
                return;
            }
        }
        this.hasThresholdCoverageGap = sorted[sorted.length - 1].maxThresholds < this.maxRiskScore;
    }

    addConsequenceRating(): void {
        const maxValue = this.consequenceRatings.length > 0
            ? Math.max(...this.consequenceRatings.map(r => r.value))
            : -1;
        this.consequenceRatings.push({
            value: maxValue + 1,
            label: String(maxValue + 1),
            description: undefined,
            reqMethodOfAssessment: MethodOfAssessment.DoesNotApply
        });
        this.onConfigChanges();
    }

    deleteConsequenceRating(index: number): void {
        if (this.consequenceRatings.length <= 1) return;
        this.consequenceRatings.splice(index, 1);
        this.onConfigChanges();
    }

    validateConsequenceRatingValue(index: number): void {
        const rating = this.consequenceRatings[index];
        if (rating.value < 0 || !Number.isInteger(rating.value)) {
            this.toastService.displayErrorMessage('Consequence Rating must be a non-negative integer', true);
            // Reset to previous valid value
            if (this.consequenceRatings.length > index) {
                this.onConfigChanges();
            }
        }
    }

    public trackByIndex = trackByIndex;
}

